/**
 * Echobot Service
 * Manages context detection, proactive events, and message generation
 */

import { echobotMessages, getRandomTip, getRandomGeneralTip, getLocalizedMessage } from '../data/echobotMessages';
import { geminiService } from './geminiService';
import { EchobotContext, EchobotContextData, GeminiEchobotResponse } from '../types/echobot';
import { getAILanguage, getAIPhrases, type SupportedLanguage } from '../utils/aiLanguageHelper';

// Session storage keys
const STORAGE_KEYS = {
  SHOWN_TIPS: 'echobot-shown-tips',
  FIRST_VISIT: 'echobot-first-visit',
  AI_CALL_COUNT: 'echobot-ai-calls',
  LAST_AI_CALL: 'echobot-last-ai-call',
  MESSAGE_CACHE: 'echobot-message-cache'
};

// Rate limiting: Max 10 AI calls per day
const MAX_AI_CALLS_PER_DAY = 10;

// Cache TTL: 1 hour
const CACHE_TTL = 60 * 60 * 1000;

interface CachedMessage {
  key: string;
  response: GeminiEchobotResponse;
  timestamp: number;
}

export const echobotService = {
  /**
   * Check if this is the user's first visit to a page
   */
  isFirstVisit(context: EchobotContext): boolean {
    const visited = sessionStorage.getItem(`${STORAGE_KEYS.FIRST_VISIT}-${context}`);
    if (!visited) {
      sessionStorage.setItem(`${STORAGE_KEYS.FIRST_VISIT}-${context}`, 'true');
      return true;
    }
    return false;
  },

  /**
   * Check if a tip has already been shown
   */
  isTipShown(tipId: string): boolean {
    const shown = sessionStorage.getItem(STORAGE_KEYS.SHOWN_TIPS);
    if (!shown) return false;
    const tips = JSON.parse(shown) as string[];
    return tips.includes(tipId);
  },

  /**
   * Mark a tip as shown
   */
  markTipShown(tipId: string) {
    const shown = sessionStorage.getItem(STORAGE_KEYS.SHOWN_TIPS);
    const tips = shown ? JSON.parse(shown) as string[] : [];
    if (!tips.includes(tipId)) {
      tips.push(tipId);
      sessionStorage.setItem(STORAGE_KEYS.SHOWN_TIPS, JSON.stringify(tips));
    }
  },

  /**
   * Check if AI rate limit is exceeded
   */
  canMakeAICall(): boolean {
    const count = localStorage.getItem(STORAGE_KEYS.AI_CALL_COUNT);
    const lastCall = localStorage.getItem(STORAGE_KEYS.LAST_AI_CALL);
    
    // Reset counter if it's a new day
    if (lastCall) {
      const lastDate = new Date(lastCall);
      const today = new Date();
      if (lastDate.getDate() !== today.getDate() || lastDate.getMonth() !== today.getMonth()) {
        localStorage.setItem(STORAGE_KEYS.AI_CALL_COUNT, '0');
        localStorage.setItem(STORAGE_KEYS.LAST_AI_CALL, today.toISOString());
        return true;
      }
    }

    const callCount = count ? parseInt(count) : 0;
    return callCount < MAX_AI_CALLS_PER_DAY;
  },

  /**
   * Increment AI call counter
   */
  incrementAICallCount() {
    const count = localStorage.getItem(STORAGE_KEYS.AI_CALL_COUNT);
    const callCount = count ? parseInt(count) : 0;
    localStorage.setItem(STORAGE_KEYS.AI_CALL_COUNT, (callCount + 1).toString());
    localStorage.setItem(STORAGE_KEYS.LAST_AI_CALL, new Date().toISOString());
  },

  /**
   * Get cached message if available and not expired
   */
  getCachedMessage(cacheKey: string): GeminiEchobotResponse | null {
    const cache = localStorage.getItem(STORAGE_KEYS.MESSAGE_CACHE);
    if (!cache) return null;

    try {
      const cached = JSON.parse(cache) as CachedMessage[];
      const message = cached.find(c => c.key === cacheKey);
      
      if (message && Date.now() - message.timestamp < CACHE_TTL) {
        return message.response;
      }
    } catch (e) {
      console.error('Failed to parse message cache:', e);
    }

    return null;
  },

  /**
   * Cache a message
   */
  cacheMessage(cacheKey: string, response: GeminiEchobotResponse) {
    const cache = localStorage.getItem(STORAGE_KEYS.MESSAGE_CACHE);
    let cached: CachedMessage[] = [];

    try {
      cached = cache ? JSON.parse(cache) : [];
    } catch (e) {
      cached = [];
    }

    // Remove expired entries and old entries (keep max 20)
    const now = Date.now();
    cached = cached.filter(c => now - c.timestamp < CACHE_TTL);
    if (cached.length > 20) {
      cached = cached.slice(-20);
    }

    // Add new cache entry
    cached.push({
      key: cacheKey,
      response,
      timestamp: now
    });

    localStorage.setItem(STORAGE_KEYS.MESSAGE_CACHE, JSON.stringify(cached));
  },

  /**
   * Get welcome message for context
   * Now supports language parameter for localized messages
   */
  getWelcomeMessage(context: EchobotContext, language?: SupportedLanguage) {
    const lang = language || getAILanguage().code;
    const localizedMsg = getLocalizedMessage('welcome', context, lang);
    if (localizedMsg) return localizedMsg;
    
    // Fallback to default messages
    const welcomeMessages = echobotMessages.welcome;
    return welcomeMessages[context as keyof typeof welcomeMessages] || welcomeMessages.dashboard;
  },

  /**
   * Get contextual tip (static fallback)
   * Now supports language parameter for localized messages
   */
  getContextualTip(context: EchobotContext, language?: SupportedLanguage) {
    const lang = language || getAILanguage().code;
    
    switch (context) {
      case 'dashboard':
        return getRandomTip(echobotMessages.dashboardTips, lang);
      case 'google-ads':
        return getRandomTip(echobotMessages.googleAdsTips, lang);
      case 'tasks':
        return getRandomTip(echobotMessages.taskTips, lang);
      case 'opportunities':
        return getRandomTip(echobotMessages.opportunityTips, lang);
      case 'leads':
        return getRandomTip(echobotMessages.leadTips, lang);
      default:
        // Check if message library has specific tips for this context
        if (context === 'emails' || context === 'stream' || context === 'settings') {
          return getRandomGeneralTip(lang);
        }
        return getRandomGeneralTip(lang);
    }
  },

  /**
   * Generate AI-powered contextual message
   * Uses caching and rate limiting
   * Always responds in the current system language
   */
  async generateContextualMessage(
    contextData: EchobotContextData,
    forceAI: boolean = false
  ): Promise<GeminiEchobotResponse> {
    // Get current system language
    const langConfig = getAILanguage();
    const phrases = getAIPhrases(langConfig.code);
    
    // Create cache key (include language to avoid mixed language cache)
    const cacheKey = `${contextData.page}-${contextData.issue || 'general'}-${langConfig.code}`;

    // Check cache first
    if (!forceAI) {
      const cached = this.getCachedMessage(cacheKey);
      if (cached) {
        console.log(`Echobot: Using cached message (${langConfig.code})`);
        return cached;
      }
    }

    // Check if Gemini is available
    if (!geminiService.isConfigured()) {
      console.warn('Echobot: Gemini not configured, using static fallback');
      const staticTip = this.getContextualTip(contextData.page, langConfig.code);
      return {
        message: staticTip.content,
        animation: staticTip.animation,
        actionButton: staticTip.actionButton
      };
    }

    // Check rate limit
    if (!forceAI && !this.canMakeAICall()) {
      console.warn('Echobot: AI rate limit reached, using static fallback');
      const staticTip = this.getContextualTip(contextData.page, langConfig.code);
      return {
        message: staticTip.content,
        animation: staticTip.animation,
        actionButton: staticTip.actionButton
      };
    }

    // Generate AI response with current system language
    try {
      const response = await geminiService.generateEchobotResponse({
        ...contextData,
        language: langConfig.code
      });
      
      // Cache and increment counter
      this.cacheMessage(cacheKey, response);
      this.incrementAICallCount();
      
      console.log(`Echobot: Generated AI message (${langConfig.code})`);
      return response;
    } catch (error) {
      console.error('Echobot: AI generation failed, using fallback:', error);
      const staticTip = this.getContextualTip(contextData.page, langConfig.code);
      return {
        message: staticTip.content,
        animation: staticTip.animation,
        actionButton: staticTip.actionButton
      };
    }
  },

  /**
   * Detect proactive events and return appropriate message
   */
  async detectProactiveEvent(
    contextData: EchobotContextData
  ): Promise<GeminiEchobotResponse | null> {
    const { organizationStats } = contextData;

    // No campaigns - suggest creating one
    if (
      contextData.page === 'google-ads' &&
      organizationStats.campaignCount === 0 &&
      !this.isTipShown('no-campaigns')
    ) {
      this.markTipShown('no-campaigns');
      return {
        message: echobotMessages.proactive.noCampaigns.content,
        animation: echobotMessages.proactive.noCampaigns.animation,
        actionButton: echobotMessages.proactive.noCampaigns.actionButton
      };
    }

    // No leads - suggest adding one
    if (
      (contextData.page === 'dashboard' || contextData.page === 'leads') &&
      organizationStats.leadCount === 0 &&
      !this.isTipShown('no-leads')
    ) {
      this.markTipShown('no-leads');
      return {
        message: echobotMessages.proactive.noLeads.content,
        animation: echobotMessages.proactive.noLeads.animation,
        actionButton: echobotMessages.proactive.noLeads.actionButton
      };
    }

    // Few opportunities - encourage creating more
    if (
      contextData.page === 'opportunities' &&
      organizationStats.opportunityCount === 0 &&
      !this.isTipShown('no-opportunities')
    ) {
      this.markTipShown('no-opportunities');
      return {
        message: 'Noch keine Opportunities? Verwandle Leads in Deals! 💰',
        animation: 'question',
        actionButton: {
          label: 'Opportunity anlegen',
          action: 'open-add-opportunity-modal'
        }
      };
    }

    // Many tasks - encourage organization
    if (
      contextData.page === 'tasks' &&
      organizationStats.taskCount && organizationStats.taskCount > 20 &&
      !this.isTipShown('many-tasks')
    ) {
      this.markTipShown('many-tasks');
      return {
        message: 'Viele Tasks! Nutze Gruppen zur Organisation! 📦',
        animation: 'question'
      };
    }

    // Low performance detected
    if (contextData.issue === 'low-ctr' && !this.isTipShown('low-performance')) {
      this.markTipShown('low-performance');
      return {
        message: echobotMessages.proactive.lowPerformance.content,
        animation: echobotMessages.proactive.lowPerformance.animation,
        actionButton: echobotMessages.proactive.lowPerformance.actionButton
      };
    }

    // Overdue tasks warning
    if (contextData.issue === 'overdue-tasks' && !this.isTipShown('overdue-tasks')) {
      this.markTipShown('overdue-tasks');
      return {
        message: echobotMessages.proactive.overdueTasks.content,
        animation: echobotMessages.proactive.overdueTasks.animation,
        actionButton: echobotMessages.proactive.overdueTasks.actionButton
      };
    }

    // Auto-detect overdue tasks from stats
    if (
      organizationStats.overdueTasks &&
      organizationStats.overdueTasks > 0 &&
      !this.isTipShown('overdue-today-' + new Date().toDateString())
    ) {
      this.markTipShown('overdue-today-' + new Date().toDateString());
      return {
        message: `⚠️ ${organizationStats.overdueTasks} überfällige Task${organizationStats.overdueTasks > 1 ? 's' : ''}! Lass uns priorisieren!`,
        animation: 'shake',
        actionButton: {
          label: 'Tasks ansehen',
          action: 'navigate-to-tasks'
        }
      };
    }

    // Inactive opportunities warning
    if (contextData.issue === 'inactive-opportunities' && !this.isTipShown('inactive-opps')) {
      this.markTipShown('inactive-opps');
      return {
        message: echobotMessages.proactive.inactiveOpportunities.content,
        animation: echobotMessages.proactive.inactiveOpportunities.animation,
        actionButton: echobotMessages.proactive.inactiveOpportunities.actionButton
      };
    }

    // Auto-detect inactive opportunities
    if (
      organizationStats.inactiveOpportunities &&
      organizationStats.inactiveOpportunities > 0 &&
      !this.isTipShown('inactive-opps-today-' + new Date().toDateString())
    ) {
      this.markTipShown('inactive-opps-today-' + new Date().toDateString());
      return {
        message: `📞 ${organizationStats.inactiveOpportunities} Opportunity${organizationStats.inactiveOpportunities > 1 ? 's' : ''} braucht${organizationStats.inactiveOpportunities === 1 ? '' : 'en'} Follow-up!`,
        animation: 'question',
        actionButton: {
          label: 'Deals prüfen',
          action: 'navigate-to-opportunities'
        }
      };
    }

    // Unassigned leads warning
    if (
      organizationStats.unassignedLeads &&
      organizationStats.unassignedLeads > 0 &&
      !this.isTipShown('unassigned-leads-' + new Date().toDateString())
    ) {
      this.markTipShown('unassigned-leads-' + new Date().toDateString());
      return {
        message: `👤 ${organizationStats.unassignedLeads} Lead${organizationStats.unassignedLeads > 1 ? 's' : ''} ohne Zuweisung! Wer kümmert sich?`,
        animation: 'question',
        actionButton: {
          label: 'Leads zuweisen',
          action: 'navigate-to-leads'
        }
      };
    }

    return null;
  },

  /**
   * Get milestone message
   */
  getMilestoneMessage(milestone: 'first_campaign' | 'ten_leads' | 'first_deal' | 'hundred_tasks') {
    const milestones = echobotMessages.milestones;
    
    switch (milestone) {
      case 'first_campaign':
        return milestones.firstCampaign;
      case 'ten_leads':
        return milestones.tenLeads;
      case 'first_deal':
        return milestones.firstDeal;
      case 'hundred_tasks':
        return milestones.hundredTasks;
      default:
        return null;
    }
  },

  /**
   * Get success message
   */
  getSuccessMessage(action: 'campaign_created' | 'lead_created' | 'task_completed' | 'deal_closed') {
    const success = echobotMessages.success;
    
    switch (action) {
      case 'campaign_created':
        return success.campaignCreated;
      case 'lead_created':
        return success.leadCreated;
      case 'task_completed':
        return success.taskCompleted;
      case 'deal_closed':
        return success.dealClosed;
      default:
        return null;
    }
  },

  /**
   * Get error message
   */
  getErrorMessage(errorType: 'generic' | 'api' | 'no_permission') {
    const errors = echobotMessages.errors;
    
    switch (errorType) {
      case 'api':
        return errors.apiError;
      case 'no_permission':
        return errors.noPermission;
      default:
        return errors.generic;
    }
  },

  /**
   * Get onboarding message for specific step
   */
  getOnboardingMessage(step: number | string) {
    const onboarding = echobotMessages.onboarding;
    
    if (typeof step === 'number') {
      const stepKey = `step${step}` as keyof typeof onboarding;
      return onboarding[stepKey] || onboarding.step1;
    }
    
    return onboarding[step as keyof typeof onboarding] || onboarding.step1;
  },

  /**
   * Clear all shown tips (useful for testing)
   */
  clearShownTips() {
    sessionStorage.removeItem(STORAGE_KEYS.SHOWN_TIPS);
  },

  /**
   * Clear message cache
   */
  clearMessageCache() {
    localStorage.removeItem(STORAGE_KEYS.MESSAGE_CACHE);
  },

  /**
   * Get AI call stats
   */
  getAICallStats() {
    const count = localStorage.getItem(STORAGE_KEYS.AI_CALL_COUNT);
    const lastCall = localStorage.getItem(STORAGE_KEYS.LAST_AI_CALL);
    
    return {
      count: count ? parseInt(count) : 0,
      max: MAX_AI_CALLS_PER_DAY,
      remaining: MAX_AI_CALLS_PER_DAY - (count ? parseInt(count) : 0),
      lastCall: lastCall ? new Date(lastCall) : null
    };
  }
};

